import { Col, Form, Input, Row, Select } from "antd";
import React, { useEffect, useState } from "react";
import { DraggableModal } from "../../../../components/DraggableModal";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { SelectProductType, SelectTerritorialUnits, SelectUnitMeasurement } from "../../../../components/selects";
import { EntTradeRegistry } from "../../../../models/organic/tradeRegistry";
import SelectCitizenship from "../../../../components/selects/SelectCitizenship";

enum FormField {
  id,
  year,
  month,
  tradeType,
  citizenshipId,
  productTypeId,
  unitOfMeasurementId,
  quantity,
  value,
  regionOfDispatchId,
  regionOfArrivalId
}


interface IProps {
  show: boolean;
  item: EntTradeRegistry;
  onHide: () => void;
  onSubmit: (pos: EntTradeRegistry) => Promise<boolean>;
}

const months = [
  { value: 1, label: "Январь" },
  { value: 2, label: "Февраль" },
  { value: 3, label: "Март" },
  { value: 4, label: "Апрель" },
  { value: 5, label: "Май" },
  { value: 6, label: "Июнь" },
  { value: 7, label: "Июль" },
  { value: 8, label: "Август" },
  { value: 9, label: "Сентябрь" },
  { value: 10, label: "Октябрь" },
  { value: 11, label: "Ноябрь" },
  { value: 12, label: "Декабрь" },
];

const operationOptions = [
  { label: "Импорт", value: "IMPORT" },
  { label: "Экспорт", value: "EXPORT" },
];

export const AddTradeRegistryModal: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
	if (props.show) setFieldsFromEntity();
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.year, value: props.item.year },
	  { name: FormField.month, value: props.item.month },
	  { name: FormField.tradeType, value: props.item.tradeType },
	  { name: FormField.citizenshipId, value: props.item.citizenship?.id },
	  { name: FormField.productTypeId, value: props.item.productType?.id },
	  { name: FormField.unitOfMeasurementId, value: props.item.unitOfMeasurement?.id },
	  { name: FormField.quantity, value: props.item.quantity },
	  { name: FormField.value, value: props.item.value },
	  { name: FormField.regionOfDispatchId, value: props.item.regionOfDispatch?.id },
	  { name: FormField.regionOfArrivalId, value: props.item.regionOfArrival?.id },
	]);
  };

  const setEntityFromFields = (): EntTradeRegistry => {
	let res = _.cloneDeep(props.item);
	res.year = form.getFieldValue(FormField.year);
	res.month = form.getFieldValue(FormField.month);
	res.tradeType = form.getFieldValue(FormField.tradeType);
	res.citizenshipId = form.getFieldValue(FormField.citizenshipId);
	res.productTypeId = form.getFieldValue(FormField.productTypeId);
	res.unitOfMeasurementId = form.getFieldValue(FormField.unitOfMeasurementId);
	res.quantity = form.getFieldValue(FormField.quantity);
	res.value = form.getFieldValue(FormField.value);
	res.regionOfDispatchId = form.getFieldValue(FormField.regionOfDispatchId);
	res.regionOfArrivalId = form.getFieldValue(FormField.regionOfArrivalId);
	return res;
  }

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  const data = setEntityFromFields();
	  const result = await props.onSubmit(data);
	  if (result) props.onHide()
	} finally {
	  setLoading(false);
	}
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index);

  return (
	<>
	  <DraggableModal
		title={ props.item.id === null ? t('general.add') : t('general.edit') }
		visible={ props.show }
		onCancel={ props.onHide }
		onSubmit={ handleSubmit }
		submitLoading={ loading }
		width={ 800 }
	  >
		<Form
		  initialValues={ { remember: true } }
		  autoComplete="off"
		  layout="vertical"
		  form={ form }
		>
		  <Row style={ { margin: 0 } } gutter={ 16 }>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('Год') }
				name={ FormField.year }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Select
				  placeholder={ t("msg.selectPlaceholder") }
				  allowClear
				>
				  { years.map((year) => (
					<Select.Option key={ year } value={ year }>
					  { year }
					</Select.Option>
				  )) }
				</Select>
			  </Form.Item>
			  <Form.Item
				label={ t('Месяц') }
				name={ FormField.month }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Select
				  placeholder={ t("msg.selectPlaceholder") }
				  allowClear
				  options={ months }
				/>
			  </Form.Item>
			  <Form.Item
				label={ t('Тип торговли') }
				name={ FormField.tradeType }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Select
				  placeholder={ t("Выберите тип торговли") }
				  options={ operationOptions }
				  allowClear
				/>
			  </Form.Item>
			  <Form.Item
				label={ t('Область') }
				name={ FormField.citizenshipId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectCitizenship/>
			  </Form.Item>
			  <Form.Item
				label={ t('Тип продукта') }
				name={ FormField.productTypeId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectProductType/>
			  </Form.Item>
			</Col>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('Единица измерения') }
				name={ FormField.unitOfMeasurementId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectUnitMeasurement/>
			  </Form.Item>
			  <Form.Item
				label={ t('Количество') }
				name={ FormField.quantity }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="number"/>
			  </Form.Item>
			  <Form.Item
				label={ t('Стоимость') }
				name={ FormField.value }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="number"/>
			  </Form.Item>
			  <Form.Item
				label={ t('Регион отгрузки') }
				name={ FormField.regionOfDispatchId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnits/>
			  </Form.Item>
			  <Form.Item
				label={ t('Регион прибытия') }
				name={ FormField.regionOfArrivalId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnits/>
			  </Form.Item>
			</Col>
		  </Row>
		</Form>
	  </DraggableModal>
	</>
  )
}
